// Outgoing-call overlay — what the caller sees while the callee's phone
// rings. Same card language as IncomingCallModal so they read as a family.
// No pulsing ring here (they haven't answered yet — calmer). Auto-dismisses
// on any callManager state change.

import { PhoneOff } from 'lucide-react'
import Avatar from '@/components/Avatar'
import { useCallManager } from '@/lib/callManager'

export function OutgoingCallOverlay() {
  const { state, cancelCall } = useCallManager()
  if (state.kind !== 'outgoing-ringing') return null
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Calling ${state.calleeName}`}
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--overlay-scrim)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 500, padding: 16,
      }}
    >
      <div
        className="call-card-enter"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 20, padding: 32,
          width: '100%', maxWidth: 380,
          boxShadow: 'var(--shadow-popover-mid)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18,
        }}
      >
        {/* Label — "Calling" + animated ellipsis */}
        <div
          aria-live="polite"
          style={{
            color: 'var(--ink-tertiary)',
            display: 'inline-flex', alignItems: 'baseline', gap: 1,
          }}
        >
          <span style={{
            fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Calling
          </span>
          <span aria-hidden="true" style={{ display: 'inline-flex', gap: 1, marginLeft: 4 }}>
            <span className="call-ellipsis-dot" style={{ fontSize: 11, fontWeight: 700, animationDelay: '0ms'   }}>.</span>
            <span className="call-ellipsis-dot" style={{ fontSize: 11, fontWeight: 700, animationDelay: '200ms' }}>.</span>
            <span className="call-ellipsis-dot" style={{ fontSize: 11, fontWeight: 700, animationDelay: '400ms' }}>.</span>
          </span>
        </div>

        {/* Avatar — no pulse, calmer than incoming */}
        <div style={{ marginTop: 4 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
          }}>
            <Avatar name={state.calleeName} size={80} variant="contact" />
          </div>
        </div>

        {/* Callee name + subtitle */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          textAlign: 'center', width: '100%',
        }}>
          <p style={{
            fontSize: 19, fontWeight: 700, color: 'var(--ink-primary)',
            margin: 0, letterSpacing: '-0.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            width: '100%',
          }}>
            {state.calleeName}
          </p>
          <p style={{
            fontSize: 13, color: 'var(--ink-secondary)', margin: 0,
          }}>
            Ringing…
          </p>
        </div>

        {/* Cancel (single full-width button — muted danger to mirror Decline) */}
        <button
          type="button"
          onClick={cancelCall}
          aria-label="Cancel call"
          style={{
            width: '100%', height: 48, marginTop: 6,
            background: 'var(--danger-dim)', color: 'var(--danger)',
            border: '1px solid var(--danger-border)', borderRadius: 12,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <PhoneOff style={{ width: 16, height: 16 }} /> Cancel
        </button>
      </div>
    </div>
  )
}
